import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Segment,
  Dimmer,
  Loader,
  Header,
  Button,
  Icon,
  Input,
  Menu,
} from 'semantic-ui-react';
import useDebounceInputValue from '../hooks/useDebounceInputValue.jsx';
import useTimeout from '../hooks/useTimeout.jsx';
import moment from 'moment-timezone';

export default function ServiceCheckTextbox(props) {
  const [logRecords, setLogRecords] = useState([]);
  const [serviceStatus, setServiceStatus] = useState(
    'No service check worker running',
  );
  const [disableCheck, setDisableCheck] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, handleSearchChange] = useDebounceInputValue();

  const addFormattedLine = (record) => {
    const formatDate = moment
      .utc(record.created_at)
      .local()
      .format('ddd, MMM DD YYYY, h:mm:ss az');
    const jobLink = `/${currentCustomer}/remote_jobs/${record.job_id}`;
    const objectTag = <a href={jobLink}>(Job details)</a>;

    return {
      ...record,
      formattedLine: (
        <>
          {`[${formatDate}] ${record.message}`} {objectTag}
        </>
      ),
      query: record.message + '(Job details)',
    };
  };

  const logOutput = useMemo(() => {
    if (!search) {
      return logRecords;
    }
    // escape invalid regex characters in search before creating a regexp
    const escapedSearch = search.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
    const regexSearch = new RegExp(escapedSearch, 'i');

    return logRecords.filter((record) => record.query.match(regexSearch));
  }, [search, logRecords]);

  const triggerCheck = useCallback(() => {
    fetch(`${window.location.pathname}/trigger_service_check`)
      .then((response) => response.json())
      .then((responseData) => {
        const newStatus = responseData.run
          ? 'Worker triggered, waiting for response...'
          : responseData.error;

        setServiceStatus(newStatus);
        if (responseData.error) {
          // eslint-disable-next-line max-nested-callbacks
          useTimeout(
            () => setServiceStatus('No service check worker running'),
            1000,
          );
        }
      });
  }, []);

  useEffect(() => {
    fetch(`${window.location.pathname}/retrieve_service_log`)
      .then((response) => response.json())
      .then((data) => {
        // eslint-disable-next-line max-nested-callbacks
        setLogRecords(data.output.map((record) => addFormattedLine(record)));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (serviceStatus.match(/^(Parsing|Updating|Sending|Worker triggered)/)) {
      setDisableCheck(true);
    } else {
      setDisableCheck(false);
    }
  }, [serviceStatus]);

  useEffect(() => {
    const handler = (data) => {
      setServiceStatus(data.new_output.message);

      setLogRecords((prevLog) => [
        addFormattedLine(data.new_output),
        ...prevLog,
      ]);
    };

    Messenger.on(props.updateChannel, handler);
    return () => {
      Messenger.off(props.updateChannel, handler);
    };
  }, []);

  return (
    <Fragment>
      <Menu
        fluid
        secondary
      >
        <Menu.Item
          fitted
          header
        >
          <Header as='h3'>
            Service Check Output
            <Header.Subheader>
              Log output for all checkable properties in this Node Group
            </Header.Subheader>
          </Header>
        </Menu.Item>

        <Menu.Menu position='right'>
          <Menu.Item fitted>{serviceStatus}</Menu.Item>
          <Menu.Item fitted>
            <Button
              positive
              disabled={disableCheck}
              onClick={triggerCheck}
            >
              <Icon name='play' />
              Trigger Check
            </Button>
          </Menu.Item>
          <Menu.Item fitted>
            <Input
              placeholder='Type to filter the output'
              onChange={handleSearchChange}
            />
          </Menu.Item>
        </Menu.Menu>
      </Menu>

      <Segment className='service-check-output'>
        <Dimmer
          inverted
          active={loading}
        >
          <Loader size='medium'>Loading</Loader>
        </Dimmer>

        {logOutput.map((record) => (
          <pre key={record.id}>{record.formattedLine}</pre>
        ))}
      </Segment>
    </Fragment>
  );
}
